import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { CardNews, type CardNewsData } from "@/components/ax-news/card-news";
import { ChevronRight } from "lucide-react";
import IlNoData from "@/assets/icons/il-no-data.svg";
import type { AxNewsSectionKey } from "@/types/ax-news";
import {
  AXNewsService,
  type IGetListArticlesRes,
  type ICategory,
} from "@/services/ax-news-service";

const SECTION_KEY_TO_CATEGORY: Record<AxNewsSectionKey, ICategory> = {
  "use-case": "ai_use_case",
  trend: "ax_trend",
  "pwc-solutions": "pwc_digital_solutions",
};

const mapToCardNewsData = (article: IGetListArticlesRes): CardNewsData => ({
  id: article.id,
  image: article.cover_image_url,
  title: article.title,
  description: article.content,
  date: article.created_at,
  href: `/ax-news/list/${article.id}`,
});

const SECTIONS: { key: AxNewsSectionKey; title: string }[] = [
  { key: "use-case", title: "AI Use Case" },
  { key: "trend", title: "AX Trend" },
  { key: "pwc-solutions", title: "PwC Digital Solutions" },
];

export const AXNews = () => {
  const navigate = useNavigate();
  const [sectionData, setSectionData] = useState<Record<AxNewsSectionKey, CardNewsData[]>>({
    "use-case": [],
    trend: [],
    "pwc-solutions": [],
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // setIsLoading(true);
    Promise.all(
      SECTIONS.map((s) =>
        AXNewsService.getListByArticles({
          category: SECTION_KEY_TO_CATEGORY[s.key],
          page: 1,
          page_size: 3,
        }),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const dataMap: Record<AxNewsSectionKey, CardNewsData[]> = {
          "use-case": [],
          trend: [],
          "pwc-solutions": [],
        };
        SECTIONS.forEach((s, i) => {
          dataMap[s.key] = (results[i].data.data?.items ?? []).map(mapToCardNewsData);
        });
        setSectionData(dataMap);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const allEmpty = !isLoading && SECTIONS.every((s) => sectionData[s.key].length === 0);

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="flex flex-col gap-12 px-5 py-6 pb-10 lg:px-[100px] lg:py-10 max-w-[1440px] mx-auto w-full"
        style={{ display: isLoading ? "none" : "flex" }}
      >
      {/* header */}
      <div className="flex flex-col gap-3">
        <h1 className=" text-[44px] leading-[48px] lg:text-[56px] lg:leading-[64px] text-black font-normal tracking-[-1.68px]">
          AX News
        </h1>
        <p className="text-black">최신 AI 트렌드와 비즈니스 인사이트를 확인하세요</p>
      </div>

      {allEmpty ? (
        <div className="flex flex-col items-center lg:gap-[12px] gap-2 py-20 w-full">
          <img src={IlNoData} alt="" />
          <div className="flex flex-col items-center gap-2">
            <span className="md:text-[32px] text-[24px] font-normal leading-7 md:leading-10 tracking-[-0.03em] text-[#000000E8]">
              No data
            </span>
            <p className="lg:text-[24px] text-[16px] font-normal leading-6 tracking-[-0.02em] text-[#000000] text-center mt-[8px]">
              아직 생성된 게시글이 없습니다
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-12">
          {SECTIONS.map((section) => {
            if (sectionData[section.key].length === 0) return null;

            return (
              <div key={section.key} className="flex flex-col gap-[12px]">
                <button
                  type="button"
                  onClick={() => navigate(`/ax-news/list?section=${section.key}`)}
                  aria-label={`${section.title} 목록 보기`}
                  className="flex justify-between items-center w-full text-left group"
                >
                  <h3 className="text-[#black] font-bold text-[20px] leading-6 lg:leading-7 tracking-[-0.4px]">
                    {section.title}
                  </h3>
                  <ChevronRight
                    width="24px"
                    height="24px"
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </button>
                {/* mobile: horizontal scroll 290px cards; desktop: 3-col fill grid */}
                <div className="flex lg:grid lg:grid-cols-3 gap-4 lg:gap-6 -mx-5 px-5 lg:mx-0 lg:px-0 overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                  {sectionData[section.key].map((news) => (
                    <div
                      key={news.id}
                      className="w-[290px] lg:w-auto lg:flex-1 shrink-0 snap-center lg:max-h-[408px] max-h-[400px]"
                    >
                      <CardNews data={news} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
};

export default AXNews;
